import React from 'react';
import { useTranslation } from 'react-i18next';
import { Loader, X } from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import type { SavedConversation } from '../types';

function getTimeAgo(updatedAt: string): string {
  const diff = Date.now() - new Date(updatedAt).getTime();
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  return `${days}d ago`;
}

interface HistoryDropdownProps {
  savedConversations: SavedConversation[];
  isLoadingSessions: boolean;
  onLoadConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string, e?: React.MouseEvent) => void;
}

const HistoryDropdown: React.FC<HistoryDropdownProps> = ({
  savedConversations,
  isLoadingSessions,
  onLoadConversation,
  onDeleteConversation,
}) => {
  const { t } = useTranslation();

  if (isLoadingSessions) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader className="animate-spin h-5 w-5 text-primary" />
      </div>
    );
  }

  if (savedConversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full p-8">
        <p className="text-sm text-muted-foreground text-center">
          {t('drawer.multiAskAI.noHistory', 'No history available yet')}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center gap-3 px-6 py-3 border-b border-gray-200 dark:border-gray-700">
        <h3 className="text-sm font-medium">
          {t('drawer.multiAskAI.recentSessions', 'Recent Sessions')}
        </h3>
      </div>
      <ScrollArea className="h-64">
        <div>
          {savedConversations.map((conv) => {
            const firstUserMsg = conv.conversation.find(
              (msg) => msg.role === 'user',
            );
            const title = firstUserMsg?.content.slice(0, 50) || 'Untitled';
            const timeAgo = getTimeAgo(conv.updatedAt);

            return (
              <button
                key={conv._id}
                type="button"
                onClick={() => onLoadConversation(conv._id)}
                className="w-full text-left group flex items-center gap-3 px-6 py-2.5 mb-0.5 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors relative"
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm mb-1 line-clamp-1 font-normal">
                    {title}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span>
                      {conv.fileIds.length}{' '}
                      {conv.fileIds.length === 1 ? 'file' : 'files'}
                    </span>
                    <span>&bull;</span>
                    <span>{timeAgo}</span>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={(e) => onDeleteConversation(conv._id, e)}
                  className="opacity-0 group-hover:opacity-70 hover:opacity-100 transition-opacity"
                >
                  <X className="h-4 w-4" />
                </button>
              </button>
            );
          })}
        </div>
      </ScrollArea>
    </>
  );
};

export default HistoryDropdown;
